import React, { FC, useState, useCallback, SyntheticEvent, useMemo, useEffect } from 'react';
import { isEmpty, size, get } from 'lodash';
import { ButtonDownIcon } from '@manulife/mux-cds-icons';
import { Dropdown, DropdownType } from '../Dropdown';
import { Branding, NavMenuItem } from '../SideNav';
import { sanitizeDOM, setRelAttribute } from '@trip-ux/common';
import {
  AUTH_HEADERS,
  SUPPORTED_LOCALES,
  ORG_CATEGORY_AGENCY,
  ORG_CATEGORY_BROKER,
  HIDE_ORG_ROUTES,
  FEATURE_FLAGS
} from '@trip-ux/constants';
import { useMedia } from '../util/useMedia';
import { checkFeatureFlag, FeatureFlag, UserProfileStore } from '@trip-ux/redux-store';

export type BannerVariant = 'PRIMARY' | 'SECONDARY';

const bannerClasses: Record<BannerVariant, string> = {
  PRIMARY: 'banner-primary',
  SECONDARY: 'banner-secondary'
};

export interface OrganizationLabelConfig {
  changeOrganization: string;
  placeholder: string;
  required: string;
}

export interface OrganizationDropdownOption {
  label: string;
  value: string;
}

export interface BannerProps {
  isMobile?: boolean;
  isCollapsed?: boolean;
  isB2C?: boolean;
  referenceId?: boolean;
  manulifeBranding?: Branding;
  whiteLabelBranding: NavMenuItem;
  bannerImage?: string;
  bannerLabel?: string;
  className?: string;
  showLabel?: boolean;
  organizationLabel?: OrganizationLabelConfig;
  organizationCode?: string;
  organizationCategory?: string;
  organizationName?: string;
  hasLogo?: boolean;
  isAdmin?: boolean;
  userType?: string;
  featureFlags?: FeatureFlag[];
  organizationDropDownList?: OrganizationDropdownOption[];
  searchOrganizations?: (str: string) => [];
  checkError?: (showPageError: boolean, active: boolean, val: string) => boolean;
  organizationSelectionChange?: (val: string) => void;
  variant?: BannerVariant;
  language?: SUPPORTED_LOCALES;
  userProfile?: UserProfileStore;
}

interface UseBannerComputedValuesProps {
  variant: BannerVariant;
  bannerImage?: string;
  showLabel: boolean;
  bannerLabel: string;
  userType: string;
  featureFlags: FeatureFlag[];
  organizationLabel?: OrganizationLabelConfig;
  isAdmin: boolean;
  shouldHideButton: boolean;
  organizationCategory: string;
  hasLogo: boolean;
  isB2C?: boolean;
}

interface UseBannerStylesProps {
  hasBackgroundImage: boolean | string | undefined;
  bannerImage?: string;
  variant: BannerVariant;
  isAdmin: boolean;
  isMobile?: boolean;
  isCollapsed?: boolean;
  className: string;
  isMobileView: boolean;
  isB2C?: boolean;
  referenceId?: boolean;
  isPrimary: boolean;
}

// ============================================================================
// Custom Hooks
// ============================================================================

interface UseOrganizationSelectionProps {
  organizationCode: string;
  organizationSelectionChange?: (val: string) => void;
  searchOrganizations: (str: string) => [];
}

const useOrganizationSelection = ({
  organizationCode,
  organizationSelectionChange,
  searchOrganizations
}: UseOrganizationSelectionProps) => {
  const [organizationNameActive, setOrganizationNameActive] = useState<boolean>();
  const [organizationBoxActive, setOrganizationBoxActive] = useState<boolean>(false);
  const [selectedOrganization, setSelectedOrganization] = useState<string>(organizationCode);

  useEffect(() => {
    if (organizationCode !== selectedOrganization) {
      setSelectedOrganization(organizationCode);
    }
  }, [organizationCode]);

  useEffect(() => {
    if (
      selectedOrganization &&
      selectedOrganization !== organizationCode &&
      typeof organizationSelectionChange === 'function'
    ) {
      organizationSelectionChange(selectedOrganization);
    }
  }, [selectedOrganization]);

  useEffect(() => {
    if (!organizationBoxActive) {
      return () => {};
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        setOrganizationBoxActive(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [organizationBoxActive]);

  const organizationSearch = useCallback(
    (str: string) => {
      if (size(str) >= 1) {
        searchOrganizations(str);
      }
    },
    [searchOrganizations]
  );

  const toggleOrganizationBox = useCallback(() => {
    setOrganizationBoxActive(prev => !prev);
  }, []);

  return {
    organizationNameActive,
    setOrganizationNameActive,
    organizationBoxActive,
    selectedOrganization,
    setSelectedOrganization,
    organizationSearch,
    toggleOrganizationBox
  };
};

const useHiddenOrgRoutes = (language: SUPPORTED_LOCALES) => {
  const [shouldHideButton, setShouldHideButton] = useState<boolean>(false);
  const currentWindowPath = window.location.pathname;

  useEffect(() => {
    const hiddenOrgRoutes = HIDE_ORG_ROUTES[language] || {};
    const isHidden = Object.values(hiddenOrgRoutes).includes(currentWindowPath);
    setShouldHideButton(isHidden);
  }, [language, currentWindowPath]);

  return shouldHideButton;
};
const useBannerComputedValues = ({
  variant,
  bannerImage,
  showLabel,
  bannerLabel,
  userType,
  featureFlags,
  organizationLabel,
  isAdmin,
  shouldHideButton,
  organizationCategory,
  hasLogo,
  isB2C
}: UseBannerComputedValuesProps) => {
  const isPrimary = variant === 'PRIMARY';

  const hasBackgroundImage = useMemo(() => isPrimary && bannerImage && showLabel, [isPrimary, bannerImage, showLabel]);

  const hasBannerLabel = useMemo(
    () => isPrimary && showLabel && !isEmpty(bannerLabel),
    [isPrimary, showLabel, bannerLabel]
  );

  const hasFeatureFlag = checkFeatureFlag(featureFlags, FEATURE_FLAGS.ONE_TO_MANY);
  const shouldEnableFeature = userType === AUTH_HEADERS.LAN_ID || (userType === AUTH_HEADERS.CIAM_ID && hasFeatureFlag);

  const shouldShowOrgDropdown = useMemo(
    () =>
      Boolean(organizationLabel) &&
      isAdmin &&
      shouldEnableFeature &&
      !shouldHideButton &&
      organizationCategory !== ORG_CATEGORY_BROKER,
    [organizationLabel, isAdmin, shouldEnableFeature, shouldHideButton, organizationCategory]
  );

  const isB2BAdvisor = organizationCategory === ORG_CATEGORY_BROKER && !isB2C;
  const shouldShowLogo = hasLogo && !isB2BAdvisor;

  return {
    isPrimary,
    hasBackgroundImage,
    hasBannerLabel,
    shouldShowOrgDropdown,
    shouldShowLogo
  };
};

const useBannerStyles = ({
  hasBackgroundImage,
  bannerImage,
  variant,
  isAdmin,
  isMobile,
  isCollapsed,
  className,
  isMobileView,
  isB2C,
  referenceId,
  isPrimary
}: UseBannerStylesProps) => {
  const style = hasBackgroundImage ? { backgroundImage: `url(${bannerImage})` } : { height: 'unset' };
  const bannerClassName = bannerClasses[variant];
  const containerClass = `${!isAdmin ? 'mobileView' : ''} p-0 ${isMobile && !isCollapsed ? 'container' : ''}`;
  const bannerClass = `${isMobile && !isCollapsed ? 'b2cbanner' : 'banner'} ${className} ${bannerClassName}`;
  const logosClass = getBannerClassName(isMobileView, !!isB2C, !!referenceId, isPrimary, !!isCollapsed, isAdmin);

  return { style, containerClass, bannerClass, logosClass };
};

// ============================================================================
// Sub-Components
// ============================================================================

interface BannerImageProps {
  labelBranding: NavMenuItem | Branding;
}

const BannerImage: FC<BannerImageProps> = ({ labelBranding }) => {
  const label = get(labelBranding, 'label');
  const ariaLabel = get(labelBranding, 'ariaLabel', label);
  return (
    <div role="img" aria-label={ariaLabel}>
      <img alt={ariaLabel} src={labelBranding.logo} />
    </div>
  );
};

interface OrganizationHeaderProps {
  bannerOrganizationName: string;
  bannerOrganizationCategory: string;
  bannerWhiteLabelName?: string;
  bannerWhiteLabelPhone?: string;
  isB2CChannel?: boolean;
}

const OrganizationHeader: FC<OrganizationHeaderProps> = ({
  bannerOrganizationName,
  bannerOrganizationCategory,
  bannerWhiteLabelName,
  bannerWhiteLabelPhone,
  isB2CChannel
}) => {
  if (bannerOrganizationCategory === ORG_CATEGORY_AGENCY && bannerOrganizationName) {
    return <h4>{bannerOrganizationName}</h4>;
  }

  if (bannerOrganizationCategory === ORG_CATEGORY_BROKER) {
    const shouldDisplayText = bannerWhiteLabelName || bannerWhiteLabelPhone;
    if (!shouldDisplayText) {
      return null;
    }

    const displayText = [bannerWhiteLabelName, bannerWhiteLabelPhone].filter(Boolean).join(' ');
    const testId = isB2CChannel ? 'b2c-advisor-header' : 'b2b-advisor-header';

    return <h4 data-testid={testId}>{displayText}</h4>;
  }

  return null;
};

interface OrganizationDropdownButtonProps {
  onClick: (e: SyntheticEvent) => void;
  shouldShowButton: boolean;
}

const OrganizationDropdownButton: FC<OrganizationDropdownButtonProps> = ({ onClick, shouldShowButton }) => (
  <span className="organization-selection-drop">
    {shouldShowButton && (
      <button
        aria-label="organization-selection-button"
        data-testid="organization-selection-button"
        className="organization-selection-dropdown"
        onClick={onClick}>
        <ButtonDownIcon width={40} height={40} />
      </button>
    )}
  </span>
);

interface OrganizationSelectionPanelProps {
  organizationLabel: OrganizationLabelConfig;
  organizationCode: string;
  organizationDropDownList: OrganizationDropdownOption[];
  organizationNameActive?: boolean;
  language: SUPPORTED_LOCALES;
  onOrganizationChange: (val: string) => void;
  onAutoSuggestChange: (str: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  checkError: (showPageError: boolean, active: boolean, val: string) => boolean;
}

const OrganizationSelectionPanel: FC<OrganizationSelectionPanelProps> = ({
  organizationLabel,
  organizationCode,
  organizationDropDownList,
  organizationNameActive,
  language,
  onOrganizationChange,
  onAutoSuggestChange,
  onFocus,
  onBlur,
  checkError
}) => (
  <div className="organization-selection">
    <h2>{organizationLabel.changeOrganization}</h2>
    <Dropdown
      variant={DropdownType.AUTO_SUGGEST}
      name="organizationCode"
      placeholder={organizationLabel.placeholder}
      onChange={onOrganizationChange}
      onAutoSuggestChange={onAutoSuggestChange}
      options={organizationDropDownList}
      value={organizationCode}
      onFocus={onFocus}
      onInputBlur={onBlur}
      error={checkError(true, organizationNameActive, organizationCode) && organizationLabel.required}
      id="organizationCode"
      language={language}
    />
  </div>
);

interface BannerLogosProps {
  shouldShowLogo: boolean;
  whiteLabelBranding: NavMenuItem;
  manulifeBranding: Branding;
  organizationName: string;
  organizationCategory: string;
  whiteLabelName?: string;
  whiteLabelPhone?: string;
  isB2C?: boolean;
  isAdmin: boolean;
  isCollapsed?: boolean;
}

const BannerLogos: FC<BannerLogosProps> = ({
  shouldShowLogo,
  whiteLabelBranding,
  manulifeBranding,
  organizationName,
  organizationCategory,
  whiteLabelName,
  whiteLabelPhone,
  isB2C,
  isAdmin,
  isCollapsed
}) => {
  const headerProps = {
    bannerOrganizationName: organizationName,
    bannerOrganizationCategory: organizationCategory,
    bannerWhiteLabelName: whiteLabelName,
    bannerWhiteLabelPhone: whiteLabelPhone,
    isB2CChannel: isB2C
  };

  if (isAdmin) {
    return (
      <>
        {shouldShowLogo && <BannerImage labelBranding={whiteLabelBranding} />}
        <OrganizationHeader {...headerProps} />
      </>
    );
  }

  if (isCollapsed === false) {
    return (
      <>
        {shouldShowLogo && <BannerImage labelBranding={whiteLabelBranding} />}
        <OrganizationHeader {...headerProps} />
      </>
    );
  }

  return <BannerImage labelBranding={shouldShowLogo ? whiteLabelBranding : manulifeBranding} />;
};

interface BannerLinkProps {
  whiteLabelBranding: NavMenuItem;
  manulifeBranding: Branding;
  organizationCategory: string;
}

const BannerLink: FC<BannerLinkProps> = ({ whiteLabelBranding, manulifeBranding, organizationCategory }) => {
  const showDuoLogos = organizationCategory === ORG_CATEGORY_AGENCY || organizationCategory === ORG_CATEGORY_BROKER;

  return (
    <a
      data-testid={whiteLabelBranding.navId}
      href={whiteLabelBranding.href}
      id={whiteLabelBranding.navId}
      target={whiteLabelBranding.target}
      rel={setRelAttribute(whiteLabelBranding.href)}
      className="banner-duo-logos"
      aria-label={whiteLabelBranding.ariaLabel}>
      {showDuoLogos && <BannerImage labelBranding={manulifeBranding} />}
    </a>
  );
};

// ============================================================================
// Utility Functions
// ============================================================================

const getBannerClassName = (
  mobileViewEnabled: boolean,
  isB2CBanner: boolean,
  bannerReferenceId: boolean,
  primary: boolean,
  isBannerCollapsed: boolean,
  isAdminUser: boolean
): string => {
  if (mobileViewEnabled && isB2CBanner && bannerReferenceId) {
    return 'coverme-mobileLogo';
  }
  let bannersClassName = 'banner-logos ';
  if (primary) {
    bannersClassName += ' mb-auto';
  }
  if (!isBannerCollapsed && !isAdminUser) {
    bannersClassName += ' alignWrapper';
  }
  return bannersClassName;
};

// ============================================================================
// Main Banner Component
// ============================================================================

export const Banner: FC<BannerProps> = ({
  isCollapsed,
  isB2C,
  referenceId,
  isMobile,
  manulifeBranding = {} as Branding,
  whiteLabelBranding,
  bannerImage,
  bannerLabel = '',
  showLabel = true,
  className = '',
  isAdmin = false,
  userType = '',
  featureFlags = [],
  organizationDropDownList = [],
  searchOrganizations = () => [],
  checkError = () => false,
  organizationSelectionChange,
  organizationCode = '',
  organizationCategory = '',
  organizationName = '',
  userProfile = {},
  hasLogo = true,
  organizationLabel,
  variant = 'PRIMARY',
  language = SUPPORTED_LOCALES.ENGLISH
}) => {
  // Derived values from props
  const whiteLabelName = userProfile?.whiteLabel?.name;
  const whiteLabelPhone = userProfile?.whiteLabel?.phone;
  const isMobileView = useMedia([`(max-width: 991.98px)`], [true], false);

  // Custom hooks
  const {
    organizationNameActive,
    setOrganizationNameActive,
    organizationBoxActive,
    setSelectedOrganization,
    organizationSearch,
    toggleOrganizationBox
  } = useOrganizationSelection({
    organizationCode,
    organizationSelectionChange,
    searchOrganizations
  });

  const shouldHideButton = useHiddenOrgRoutes(language);

  // Computed values
  const { isPrimary, hasBackgroundImage, hasBannerLabel, shouldShowOrgDropdown, shouldShowLogo } =
    useBannerComputedValues({
      variant,
      bannerImage,
      showLabel,
      bannerLabel,
      userType,
      featureFlags,
      organizationLabel,
      isAdmin,
      shouldHideButton,
      organizationCategory,
      hasLogo,
      isB2C
    });

  // Styles
  const { style, containerClass, bannerClass, logosClass } = useBannerStyles({
    hasBackgroundImage,
    bannerImage,
    variant,
    isAdmin,
    isMobile,
    isCollapsed,
    className,
    isMobileView,
    isB2C,
    referenceId,
    isPrimary
  });

  // Event handlers
  const handleOrganizationFocus = useCallback(() => {
    setOrganizationNameActive(true);
  }, [setOrganizationNameActive]);

  const handleOrganizationBlur = useCallback(() => {
    setOrganizationNameActive(false);
  }, [setOrganizationNameActive]);

  // Shared props for BannerLogos
  const bannerLogosProps = {
    shouldShowLogo,
    whiteLabelBranding,
    manulifeBranding,
    organizationName,
    organizationCategory,
    whiteLabelName,
    whiteLabelPhone,
    isB2C,
    isAdmin,
    isCollapsed
  };

  const shouldShowManulifeLogo = !(isAdmin && isMobileView) && isPrimary;

  return (
    <div className={containerClass}>
      <div className={bannerClass} style={style}>
        <div className={logosClass}>
          <BannerLogos {...bannerLogosProps} />

          {!isAdmin && (
            <BannerLink
              whiteLabelBranding={whiteLabelBranding}
              manulifeBranding={manulifeBranding}
              organizationCategory={organizationCategory}
            />
          )}

          {isCollapsed !== false ? (
            <OrganizationDropdownButton onClick={toggleOrganizationBox} shouldShowButton={shouldShowOrgDropdown} />
          ) : null}

          {shouldShowManulifeLogo && <BannerImage labelBranding={manulifeBranding} />}
        </div>

        {hasBannerLabel && (
          <div className="mt-auto banner-label" dangerouslySetInnerHTML={{ __html: sanitizeDOM(bannerLabel) }} />
        )}

        {organizationBoxActive && shouldShowOrgDropdown && organizationLabel && (
          <OrganizationSelectionPanel
            organizationLabel={organizationLabel}
            organizationCode={organizationCode}
            organizationDropDownList={organizationDropDownList}
            organizationNameActive={organizationNameActive}
            language={language}
            onOrganizationChange={setSelectedOrganization}
            onAutoSuggestChange={organizationSearch}
            onFocus={handleOrganizationFocus}
            onBlur={handleOrganizationBlur}
            checkError={checkError}
          />
        )}
      </div>
    </div>
  );
};

Banner.displayName = 'Banner';
export default Banner;
